import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash';
import { firstValueFrom, Subject, debounceTime, map, takeUntil } from 'rxjs';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { SearchResultItem } from 'src/app/modules/shared/models';
import { TranslateService } from '../../../modules/core/services';
import { WidgetsService } from '@components/widgets/widgets.service';
import { SearchStore } from '../../../modules/core/store';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from '../../../modules/shared/components/spinner/spinner.component';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { ScrollSpyDirective } from "@modules/core/directives/scroll-spy.directive";
import { LoggingService } from '@modules/shared/services/logging.service';

@Component({
    selector: 'app-top-bar-search',
    templateUrl: './top-bar-search.component.html',
    styleUrls: ['./top-bar-search.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatIconModule,
        SpinnerComponent,
        NgClass,
        TranslateModule,
        ScrollSpyDirective
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class TopBarSearchComponent implements OnInit, OnDestroy {
    private readonly _translateService = inject(TranslateService);
    private readonly _router = inject(Router);
    private readonly _appStateService = inject(AppStateService);
    private readonly _elementRef = inject(ElementRef);
    private readonly _searchStore = inject(SearchStore);
    private readonly _loggingService = inject(LoggingService);
    private readonly _widgetStateService = inject(WidgetsService);

    public listResult: SearchResultItem[] = [];
    public query = new UntypedFormControl();
    public isLoading = true;
    public markedItem: SearchResultItem;

    public get showSearchResults() {
        return this.listResult?.length > 0
            && this._hasFocus
            && !this.isLoading;
    }

    public get hasFocus() {
 return this._hasFocus; 
}

    private _delayFocusOut = false;
    private _hasFocus = false;
    private _oldQuery: string;
    private readonly _noResultSearchItem: SearchResultItem;
    private readonly _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this._noResultSearchItem = {
            title: _translate('No matching results'),
            subTitle: null,
            value: '',
            tag: '',
            state: '',
            id: null,
            scrollableId: null,
            type: null,
            iconClass: null,
            matchingSearchKey: null,
            matchingQuery: null
        };
    }

    @HostListener('document:click', ['$event']) componentClick(event: Event) {
        if (!this._elementRef.nativeElement.contains(event.target)) {
            // Hide menu if click is outside component
            this._hasFocus = false;
        }
    }

    ngOnInit(): void {
        this.query.setValue('');

        // for the search
        this._listenToQueryChanges();
        this._listenToListResult();
        this._listenToGarageChange();
    }

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public navigateToItem(item: SearchResultItem): void {
        if (!item.type || !item.id) {
            return;
        }
        const type = item.type.toLowerCase();

        let outlets;
        switch (item.type) {
            case 'line':
                outlets = { 'outlet-right0': [type, item.id], 'outlet-right1': null, 'outlet-right2': null };
                break;
            case 'vehicle':
                this._widgetStateService.openVehicle(String(item.id));
                this._delayFocusOut = false;
                this._hasFocus = false;
                this._resetQueryValue();
                this._trackSearchResultClicked(item);
                return;
            default:
                throw new Error(`type: ${type} are missing in search navigation switch.`)
        }


        this._router.navigate(['', { outlets: outlets}]);
        this._delayFocusOut = false;
        this._hasFocus = false;

        this._resetQueryValue();

        this._trackSearchResultClicked(item);
    }

    public focusOut(): void {
        this._hasFocus = this._delayFocusOut;
    }

    public delayFocusOut(): void {
        this._delayFocusOut = true;
    }

    public focus(): void {
        this._hasFocus = true;
    }

    public resetQuery(): void {
        this._resetQueryValue();
    }

    public getSearchPlaceholder(): void {
        return this._translateService.instant('Search');
    }

    public navigateToMarkedItem(): void {
        if (this.markedItem) {
            this.navigateToItem(this.markedItem);
        } else if (this.listResult.length > 0) {
            this.navigateToItem(_.first(this.listResult));
        }
    }

    public markPrevious(): void {
        this._markItemByDirection(-1);
    }

    public markNext(): void {
        this._markItemByDirection(1);
    }

    private _markItemByDirection(dir: number): void {
        const prevIndex = _.indexOf(this.listResult, this.markedItem);
        if (prevIndex + dir > this.listResult.length - 1 || prevIndex + dir < 0) {
            dir = 0;
        }
        this.markedItem = this.listResult[prevIndex + dir];

        if (this.markedItem?.scrollableId) {
            this._scrollToSelectorId(this.markedItem.scrollableId)
        }
    }

    private _resetQueryValue(): void {
        this._oldQuery = undefined;
        this.query.setValue('', { emitEvent: false });
        this.listResult = [];
        this._searchStore.abortLastSearch();
        this.markedItem = undefined;
    }

    private _listenToQueryChanges(): void {
        this.query.valueChanges.pipe(
            debounceTime(300),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((query: string) => {
            this.isLoading = true;
            this._hasFocus = true;

            if (query === '' || query == undefined) {
                this._oldQuery = undefined;
                this.resetQuery();
                return;
            }

            query = query.trim();
            if (query === '') {
                this.listResult = [];
                this.isLoading = false;
                this._oldQuery = undefined;
            } else if (this._oldQuery !== query) {
                this.listResult = [];
                this._oldQuery = query;

                // isLoading is handled by search result listener, so it does not need to be set here
                this._search(query);
            } else {
                this.isLoading = false;
            }
        });
    }

    private _search(query: string): void {
        if (query.length < 1) return;
        this._searchStore.searchAsync(query);
    }

    private _listenToListResult(): void {
        this._searchStore.getPropertyState('searchResults').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(items => {
            this._addSearchResultItems(items);
            this.isLoading = false;

            this._trackSearchResultLoaded(items);
        });
    }

    private _addSearchResultItems(items: SearchResultItem[]): void {
        this.listResult = this.listResult.concat(items);

        if (!this.isLoading && this.listResult.length === 0) {
            this.listResult.push(this._noResultSearchItem);
        } 
    }

    private async _trackSearchResultLoaded(items: SearchResultItem[]): Promise<void> {
        var trafficAreas = await firstValueFrom(this._appStateService.getSelectedGaragesState().pipe(
            map(garages => garages.map(g => g.trafficAreaName).filter((val, idx, arr) => arr.indexOf(val) === idx))
        ));
        for (const trafficArea of trafficAreas) {
            this._loggingService.logMetric('TopBarSearch.Result.Loaded.CountPerTrafficArea', 1, {
                'Category': 'Search',
                'Subcategory': items.length > 0 ? 'WithResults' : 'NoResults',
                'TrafficArea': trafficArea,
            });
        }
    }

    private async _trackSearchResultClicked(item: SearchResultItem): Promise<void> {
        var trafficAreas = await firstValueFrom(this._appStateService.getSelectedGaragesState().pipe(
            map(garages => garages.map(g => g.trafficAreaName).filter((val, idx, arr) => arr.indexOf(val) === idx))
        ));
        for (const trafficArea of trafficAreas) {
            this._loggingService.logMetric('TopBarSearch.Result.Clicked.CountPerTrafficArea', 1, {
                'Category': 'Search',
                'Subcategory': item.type,
                'TrafficArea': trafficArea,
            });
        }
    }

    private _listenToGarageChange(): void {
        this._appStateService.getPropertyState('selectedGarageCodes').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(() => this._resetQueryValue());
    }

    /**
     * Scrolls to an item in the html, where the [attr.id] matches the selectorId input.
     *
     * @param selectorId - The id that matches the [attr.id] tag.
     * @private
     */
    private _scrollToSelectorId(selectorId: string): void  {
        if (selectorId == undefined || selectorId.trim() === '') {
            return;
        }

        const targetElement = this._elementRef.nativeElement?.querySelector('#i' + CSS.escape(selectorId));
        if (targetElement) {
            targetElement.scrollIntoView({behavior: 'auto', block: 'nearest'});
        }
    }
}